import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, CheckCircle, XCircle, Target } from 'lucide-react';
import { Resource, UIComponent } from '../../types';

interface TopologyCanvasComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface TopologyObject {
  id: string;
  type: string;
  position: 'dedans' | 'dehors';
  color: string;
  emoji: string;
  x: number;
  y: number;
  isPlaced: boolean;
  correctPosition: 'dedans' | 'dehors';
}

interface Zone {
  id: string;
  type: 'circle' | 'square' | 'triangle';
  x: number;
  y: number;
  radius: number;
  color: string;
  label: string;
}

export const TopologyCanvasComponent: React.FC<TopologyCanvasComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const [objects, setObjects] = useState<TopologyObject[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedObject, setSelectedObject] = useState<TopologyObject | null>(null);
  const [currentInstruction, setCurrentInstruction] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [placedObjects, setPlacedObjects] = useState<number>(0);
  const [correctPlacements, setCorrectPlacements] = useState<number>(0);

  // Canvas dimensions
  const canvasWidth = 600;
  const canvasHeight = 400;
  
  // Get settings from resource
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;
  const question = resource.question || 'Placez les objets dedans ou dehors du cercle';
  
  // Initialize zones (default: one circle in the center)
  const initializeZones = useCallback(() => {
    const defaultZones: Zone[] = [
      {
        id: 'zone1',
        type: 'circle',
        x: canvasWidth / 2,
        y: canvasHeight / 2,
        radius: 120,
        color: '#3B82F6',
        label: 'Zone délimitée'
      }
    ];
    setZones(defaultZones);
  }, [canvasWidth, canvasHeight]);

  // Initialize objects from resource
  const initializeObjects = useCallback(() => {
    const objectConfigs = resource.objects || [];
    const newObjects: TopologyObject[] = objectConfigs.map((config: any, index: number) => ({
      ...config,
      id: config.id || `object_${index}`,
      x: 50 + (index * 50), // Initial position on the left, smaller spacing
      y: 50,
      isPlaced: false,
      correctPosition: config.position === 'dedans' ? 'dedans' : 'dehors'
    }));
    
    setObjects(newObjects);
    setPlacedObjects(0);
    setCorrectPlacements(0);
  }, [resource.objects]);

  // Generate instructions based on objects
  const generateInstructions = useCallback(() => {
    if (objects.length === 0) return;
    
    // Find the next unplaced object
    const nextUnplacedObject = objects.find(obj => !obj.isPlaced);
    
    if (nextUnplacedObject) {
      const position = nextUnplacedObject.correctPosition;
      const emoji = nextUnplacedObject.emoji;
      setCurrentInstruction(`Placez ${emoji} ${position === 'dedans' ? 'dedans' : 'dehors'} le cercle`);
    } else {
      // All objects are placed
      setCurrentInstruction('Tous les objets sont placés ! Cliquez sur "Valider" pour vérifier vos placements.');
    }
  }, [objects]);

  // Check if a point is inside a zone
  const isPointInZone = useCallback((x: number, y: number, zone: Zone): boolean => {
    if (zone.type === 'circle') {
      const distance = Math.sqrt((x - zone.x) ** 2 + (y - zone.y) ** 2);
      return distance <= zone.radius;
    }
    // Add support for other zone types if needed
    return false;
  }, []);

  // Validate object placement with detailed feedback
  const validatePlacement = useCallback((object: TopologyObject, x: number, y: number): { 
    isValid: boolean; 
    message: string; 
    actualPosition: 'dedans' | 'dehors';
    expectedPosition: 'dedans' | 'dehors';
  } => {
    const zone = zones[0]; // Use the first zone for now
    if (!zone) {
      return {
        isValid: false,
        message: 'Aucune zone configurée',
        actualPosition: 'dehors',
        expectedPosition: object.correctPosition
      };
    }
    
    // Check if coordinates are within canvas bounds
    if (x < 0 || x > canvasWidth || y < 0 || y > canvasHeight) {
      return {
        isValid: false,
        message: 'Position en dehors du canvas',
        actualPosition: 'dehors',
        expectedPosition: object.correctPosition
      };
    }
    
    const isInside = isPointInZone(x, y, zone);
    const actualPosition = isInside ? 'dedans' : 'dehors';
    const expectedPosition = object.correctPosition;
    const isValid = actualPosition === expectedPosition;
    
    let message = '';
    if (isValid) {
      message = `Parfait ! ${object.emoji} est bien placé ${actualPosition} le cercle.`;
    } else {
      message = `${object.emoji} est placé ${actualPosition} le cercle, mais il devrait être ${expectedPosition}.`;
    }
    
    return {
      isValid,
      message,
      actualPosition,
      expectedPosition
    };
  }, [zones, isPointInZone, canvasWidth, canvasHeight]);

  // Show feedback message
  const showFeedbackMessage = useCallback((message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    
    setTimeout(() => {
      setShowFeedback(false);
    }, 3000);
  }, []);

  // Handle canvas click
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!selectedObject || !allowInteraction) return;
    
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate the scale factor between the displayed canvas and the actual canvas size
    const scaleX = canvasWidth / rect.width;
    const scaleY = canvasHeight / rect.height;
    
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;
    
    // Place object
    const updatedObjects = objects.map(obj => 
      obj.id === selectedObject.id 
        ? { ...obj, x, y, isPlaced: true }
        : obj
    );
    
    setObjects(updatedObjects);
    setSelectedObject(null);
    
    // Validate the placement with detailed feedback
    const validation = validatePlacement(selectedObject, x, y);
    const newCorrectPlacements = correctPlacements + (validation.isValid ? 1 : 0);
    const newPlacedObjects = placedObjects + 1;
    
    setCorrectPlacements(newCorrectPlacements);
    setPlacedObjects(newPlacedObjects);
    
    // Show detailed feedback
    showFeedbackMessage(validation.message, validation.isValid ? 'success' : 'error');
    
    onAction?.('object_placed', { 
      objectId: selectedObject.id, 
      position: validation.actualPosition,
      expectedPosition: validation.expectedPosition,
      isCorrect: validation.isValid,
      x,
      y,
      message: validation.message
    });
    
    // Move to next instruction if all objects are placed
    if (newPlacedObjects === objects.length) {
      onAction?.('all_objects_placed', {
        totalObjects: objects.length,
        correctPlacements: newCorrectPlacements,
        accuracy: (newCorrectPlacements / objects.length) * 100
      });
    }
  }, [selectedObject, allowInteraction, objects, correctPlacements, placedObjects, validatePlacement, onAction, showFeedbackMessage]);

  // Validate all placements
  const validateAllPlacements = useCallback(() => {
    const placedObjectsList = objects.filter(obj => obj.isPlaced);
    if (placedObjectsList.length === 0) {
      showFeedbackMessage('Aucun objet placé pour valider', 'error');
      return;
    }
    
    let totalCorrect = 0;
    const validationResults = placedObjectsList.map(obj => {
      const validation = validatePlacement(obj, obj.x, obj.y);
      if (validation.isValid) totalCorrect++;
      return {
        object: obj,
        validation
      };
    });
    
    const accuracy = (totalCorrect / placedObjectsList.length) * 100;
    const message = `Validation terminée ! ${totalCorrect}/${placedObjectsList.length} objets correctement placés (${accuracy.toFixed(0)}%)`;
    
    showFeedbackMessage(message, accuracy >= 70 ? 'success' : 'error');
    
    onAction?.('validation_completed', {
      totalObjects: placedObjectsList.length,
      correctPlacements: totalCorrect,
      accuracy,
      results: validationResults
    });
  }, [objects, validatePlacement, showFeedbackMessage, onAction]);

  // Reset the canvas
  const handleReset = useCallback(() => {
    initializeObjects();
    setSelectedObject(null);
    setShowFeedback(false);
    onAction?.('reset');
  }, [initializeObjects, onAction]);

  // Move to next instruction (unused for now)
  // const handleNextInstruction = useCallback(() => {
  //   if (instructionIndex < objects.length - 1) {
  //     setInstructionIndex(instructionIndex + 1);
  //   }
  // }, [instructionIndex, objects.length]);

  // Initialize component
  useEffect(() => {
    initializeZones();
    initializeObjects();
  }, [initializeZones, initializeObjects]);

  // Update instructions when objects change
  useEffect(() => {
    generateInstructions();
  }, [generateInstructions]);

  // Draw canvas
  const drawCanvas = useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    
    // Draw zones
    zones.forEach(zone => {
      ctx.strokeStyle = zone.color;
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]); // Dashed line
      
      if (zone.type === 'circle') {
        ctx.beginPath();
        ctx.arc(zone.x, zone.y, zone.radius, 0, 2 * Math.PI);
        ctx.stroke();
        
        // Draw zone label
        ctx.fillStyle = zone.color;
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(zone.label, zone.x, zone.y - zone.radius - 15);
      }
    });
    
    // Draw objects
    objects.forEach(obj => {
      if (obj.isPlaced) {
        // Validate placement to determine color
        const validation = validatePlacement(obj, obj.x, obj.y);
        
        // Draw object at its placed position with smaller size
        ctx.font = '20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(obj.emoji, obj.x, obj.y);
        
        // Draw a circle around the object with validation color
        ctx.strokeStyle = validation.isValid ? '#10B981' : '#EF4444'; // Green for correct, red for incorrect
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(obj.x, obj.y, 12, 0, 2 * Math.PI);
        ctx.stroke();
        
        // Add a small indicator for validation status
        if (validation.isValid) {
          ctx.fillStyle = '#10B981';
          ctx.beginPath();
          ctx.arc(obj.x + 8, obj.y - 8, 4, 0, 2 * Math.PI);
          ctx.fill();
        } else {
          ctx.fillStyle = '#EF4444';
          ctx.beginPath();
          ctx.arc(obj.x + 8, obj.y - 8, 4, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
    });
  }, [zones, objects, canvasWidth, canvasHeight, validatePlacement]);

  // Canvas ref
  const canvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    if (canvas) {
      drawCanvas(canvas);
    }
  }, [drawCanvas]);

  // Redraw when objects or zones change
  useEffect(() => {
    const canvas = document.getElementById('topology-canvas') as HTMLCanvasElement;
    if (canvas) {
      drawCanvas(canvas);
    }
  }, [objects, zones, drawCanvas]);

  return (
    <div className="topology-canvas-container p-4 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {resource.name || 'Canvas Topologie'}
        </h3>
        <p className="text-sm text-gray-600 mb-3">{question}</p>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={validateAllPlacements}
            className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white hover:bg-green-600 rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Valider
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Objets placés: {placedObjects}/{objects.length}</span>
          <span>Corrects: {correctPlacements}/{placedObjects}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(placedObjects / objects.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current instruction */}
      {currentInstruction && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            <span className="text-blue-800 font-medium">{currentInstruction}</span>
          </div>
        </div>
      )}

      {/* Canvas - Full width */}
      <div className="mb-4">
        <canvas
          id="topology-canvas"
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onClick={handleCanvasClick}
          className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair hover:border-blue-400 transition-colors"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>

      {/* Object panel - Below canvas */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Objets à placer</h4>
        <div className="flex flex-wrap gap-3 justify-center">
          {objects.map((obj) => (
            <div
              key={obj.id}
              onClick={() => !obj.isPlaced && setSelectedObject(obj)}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                obj.isPlaced
                  ? 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                  : selectedObject?.id === obj.id
                  ? 'bg-blue-100 border-blue-400'
                  : 'bg-white border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-1">{obj.emoji}</div>
                <div className="text-xs text-gray-600">
                  {obj.correctPosition === 'dedans' ? 'Dedans' : 'Dehors'}
                </div>
                {obj.isPlaced && (
                  <CheckCircle className="w-4 h-4 text-green-500 mx-auto mt-1" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback */}
      {showFeedback && (
        <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
          feedbackType === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {feedbackType === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Instructions</h4>
        <ol className="text-sm text-gray-600 space-y-1">
          <li>1. Cliquez sur un objet dans le panneau de droite</li>
          <li>2. Cliquez sur le canvas pour le placer</li>
          <li>3. Placez l'objet dedans ou dehors selon l'instruction</li>
          <li>4. Continuez jusqu'à ce que tous les objets soient placés</li>
        </ol>
      </div>
    </div>
  );
};
